import { readonly, ref, Ref } from "@vue/composition-api";
import axios from "@/libs/axios";
// @ts-ignore
import { isEqual } from "lodash";
// @ts-ignore
import { getUserData } from "@/auth/getUserData";
import ActiveChatInterface from "@/interfaces/inbox/activeChat.interface";
import ActiveChatLogEntryInterface from "@/interfaces/inbox/activeChatLogEntry.interface";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import { ChatLogEntryTypeType } from "@/types/chatLogEntryType.type";
import { InboxModuleType } from "@/types/inboxModule.type";

const loading = ref(false);
const state: Ref<ActiveChatInterface | undefined> = ref();
const page = ref(1);

function lastEntry<T>(arr: T[]): T {
    return arr[arr.length - 1];
}

export default function useActiveChat() {
    const { publicApiEndpointPath, inboxModuleType } = useInboxModuleType();
    const chatLog = ref([]);

    const fetchChat = (id: number): Promise<ActiveChatInterface> => {
        return axios
            .get(`/${publicApiEndpointPath.value}/${id}`, {
                params: {
                    fields: "id,channelId,identityId,end,status,assignedUser,assignedTeam",
                    page: page.value,
                    limit: 1, // @TODO should be a const ?
                },
            })
            .then((response) => response.data);
    };

    // @ts-ignore
    const loadActiveChat = (
        id: number,
        channelId: number,
        identityId: number,
        options: { isInitialLoading: boolean } = { isInitialLoading: true }
    ) => {
        page.value = 1;
        loading.value = options.isInitialLoading;
        chatLog.value = [];

        return fetchChat(id)
            .then((response) => {
                if (
                    inboxModuleType.value === InboxModuleType.MESSAGING &&
                    (!state.value?.identity?.id || state.value.identity.id !== id)
                ) {
                    return (state.value = response);
                }

                if (
                    inboxModuleType.value === InboxModuleType.EMAIL &&
                    (!state.value?.chatLog?.[0].id || state.value.chatLog[0].id !== id)
                ) {
                    return (state.value = response);
                }

                const originalEntriesFromLastConversation: ActiveChatLogEntryInterface[] =
                    lastEntry(state.value?.chatLog ?? [])?.entries ?? [];

                const newState = {
                    chatLog: [],
                    ...response,
                };
                const responseChatLogEntryIds =
                    lastEntry(newState.chatLog ?? [])?.entries.map((entry) => entry.id) ?? [];

                lastEntry(newState.chatLog ?? [])?.entries.push(
                    ...originalEntriesFromLastConversation.filter(
                        (chatLogEntry) => !responseChatLogEntryIds.includes(chatLogEntry.id)
                    )
                );
                state.value = newState;

                return state.value;
            })
            .finally(() => (loading.value = false));
    };

    const closeActiveChat = (chatId?: number) => {
        if ((!chatId || findActiveChatLastConversation()?.data.id === chatId) && state.value) {
            state.value = undefined;
        }
    };

    const addActiveChatEntry = (entryPayload: Partial<ActiveChatLogEntryInterface>): void => {
        if (!state.value?.chatLog) {
            return;
        }

        const chat = state.value.chatLog[state.value.chatLog.length - 1];

        if (chat) {
            // @ts-ignore
            chat.entries.push(entryPayload);
        }
    };

    const updateActiveChat = (chatData: ActiveChatInterface) => {
        return new Promise((resolve) => {
            // @ts-ignore
            if (!state.value) {
                return resolve(false);
            }

            // @ts-ignore
            const updatedData = { ...state.value, ...chatData };
            if (isEqual(updatedData, state.value)) {
                return resolve({
                    originalData: state.value,
                    updatedData: undefined,
                });
            }

            const originalData = state.value;

            // Set the active chat.
            state.value = updatedData;

            return resolve({
                originalData,
                updatedData,
            });
        });
    };

    const findActiveChatLastConversation = (): { index: number; data: Record<string, unknown> } | undefined => {
        if (!state.value?.chatLog) {
            return;
        }

        const lastConversation = state.value.chatLog[state.value.chatLog.length - 1];
        const findConversationIndex = state.value.chatLog.findIndex(
            (conversation: { id: number }) => conversation.id === lastConversation?.id
        );

        if (typeof findConversationIndex === "undefined" || findConversationIndex === -1) {
            return;
        }

        return {
            index: findConversationIndex,
            // @ts-ignore
            data: state.value.chatLog[findConversationIndex],
        };
    };

    const findIndicesToEntry = (
        entryId: number,
        entryType: ChatLogEntryTypeType,
        time: Date
    ): { chatLogIndex: number; entryIndex: number } => {
        const chatLogIndex =
            state.value?.chatLog?.findIndex((conversation, index) => {
                let end = conversation.end;
                if (index + 1 < state.value!.chatLog!.length) {
                    end = state.value!.chatLog![index + 1].start;
                }
                return (
                    (index === 0 || time.getTime() - new Date(conversation.start).getTime() >= 0) &&
                    (!end || time.getTime() - new Date(end).getTime() < 0)
                );
            }) ?? -1;
        if (!~chatLogIndex) {
            return {
                chatLogIndex: -1,
                entryIndex: -1,
            };
        }

        const entryIndex = state.value!.chatLog![chatLogIndex].entries.findIndex(
            (entry: ActiveChatLogEntryInterface) => entry.id === entryId && entry.type === entryType
        );
        return {
            chatLogIndex,
            entryIndex,
        };
    };

    /**
     * Update an entry in the active chat.
     * @param entryId Id of the entry
     * @param entryType Type of the entry
     * @param time Time of the creation of the entry (To speed up searching slightly by excluding conversations based on their start/end times)
     * @param entryUpdates The updates to the entry
     */
    const updateActiveChatLogEntry = (
        entryId: number,
        entryType: ChatLogEntryTypeType,
        time: Date,
        entryUpdates: Partial<ActiveChatLogEntryInterface>
    ): void => {
        const indices = findIndicesToEntry(entryId, entryType, time);
        if (!~indices.chatLogIndex || !~indices.entryIndex) {
            return;
        }

        state.value!.chatLog![indices.chatLogIndex].entries.splice(indices.entryIndex, 1, {
            ...state.value!.chatLog![indices.chatLogIndex].entries[indices.entryIndex],
            ...entryUpdates,
        });
    };

    /**
     * Delete an entry in the active chat.
     * @param entryId Id of the entry
     * @param entryType Type of the entry
     * @param time Time of the creation of the entry (To speed up searching slightly by excluding conversations based on their start/end times)
     */
    const deleteActiveChatLogEntry = (entryId: number, entryType: ChatLogEntryTypeType, time: Date): void => {
        const indices = findIndicesToEntry(entryId, entryType, time);
        if (!~indices.chatLogIndex || !~indices.entryIndex) {
            return;
        }

        state.value!.chatLog![indices.chatLogIndex].entries.splice(indices.entryIndex, 1);
    };

    return {
        isLoading: readonly(loading),
        getActiveChat: readonly(state),
        loadActiveChat,
        addActiveChatEntry,
        closeActiveChat,
        updateActiveChat,
        findActiveChatLastConversation,
        updateActiveChatLogEntry,
        deleteActiveChatLogEntry,
        page,
    };
}
